import TabButtons from '@/components/atoms/TabButtons';
import CustomTable from '@/components/molecules/CustomTable';
import SidebarItemViewTemplate from '@/components/templates/SidebarItemViewTemplate';
import { MIN_CHARS_TO_DO_SEARCH } from '@/constants/numberVars';
import useAdminContentList from '@/hooks/content/useAdminContentList';
import ContentModerationFilters from '@/modules/content/moderation/ContentModerationFilters';
import {
  ContentOriginBadge,
  ContentStatusBadge,
  ContentTypeBadge,
  formatContentDate,
  getContentDomainsLabel,
  getContentVisibilityLabel,
} from '@/modules/content/common/contentDisplay';
import { TSortingDirection } from '@/types';
import {
  CONTENT_STATUS_FILTERS,
  CONTENT_TYPES,
  CONTENT_VISIBILITY_FILTERS,
  TContentListItem,
  TContentStatusFilter,
  TContentType,
  TContentVisibilityFilter,
} from '@/types/Content.types';
import { HStack } from '@chakra-ui/react';
import { createFileRoute } from '@tanstack/react-router';
import React, { useMemo } from 'react';

const COLUMNS = ['Title', 'Author', 'Origin', 'Type', 'Domain', 'Visibility', 'Updated', 'Status'];

// Maps sortable column headers to backend sort fields (whitelisted server-side).
const SORT_FIELD_BY_COLUMN: Record<string, string> = {
  Title: 'title',
  Type: 'type',
  Updated: 'updatedAt',
  Status: 'status',
};
const SORT_COLUMN_BY_FIELD: Record<string, string> = {
  title: 'Title',
  type: 'Type',
  updatedAt: 'Updated',
  status: 'Status',
};
const SORTABLE_COLUMNS = ['Title', 'Type', 'Updated', 'Status'] as const;
const DEFAULT_SORT_FIELD = 'updatedAt';
const DEFAULT_SORT_DIRECTION: TSortingDirection = 'desc';
const SORT_PARAM_REGEX = /^(title|type|updatedAt|status):(asc|desc)$/;

// All params optional so the route can be reached without search params
// (sidebar link, "back" button, row clicks).
type ContentModerationSearch = {
  status?: TContentStatusFilter;
  q?: string;
  type?: TContentType;
  visibility?: TContentVisibilityFilter;
  sort?: string;
};

const DEFAULT_STATUS: TContentStatusFilter = 'published';

const ContentModeration = () => {
  const { status: statusParam, q, type, visibility: visibilityParam, sort } = Route.useSearch();
  const navigate = Route.useNavigate();

  const status = statusParam ?? DEFAULT_STATUS;
  const visibility = visibilityParam ?? 'all';
  const filterText = q ?? '';

  // Sort lives in the URL as `<field>:<dir>`; default to updatedAt desc.
  const [sortField, sortDirection] = (
    sort && SORT_PARAM_REGEX.test(sort) ? sort : `${DEFAULT_SORT_FIELD}:${DEFAULT_SORT_DIRECTION}`
  ).split(':') as [string, TSortingDirection];
  const sortingColumn = SORT_COLUMN_BY_FIELD[sortField];

  const setStatus = (next: TContentStatusFilter) => navigate({ search: (prev) => ({ ...prev, status: next }) });

  const setSearchText = (value: string) => navigate({ search: (prev) => ({ ...prev, q: value ? value : undefined }) });

  const setType = (value?: TContentType) => navigate({ search: (prev) => ({ ...prev, type: value }) });

  const setVisibility = (value: TContentVisibilityFilter) =>
    navigate({ search: (prev) => ({ ...prev, visibility: value === 'all' ? undefined : value }) });

  const updateSortingColumn = (column: string) => {
    const field = SORT_FIELD_BY_COLUMN[column];
    if (!field) return;
    const nextDirection: TSortingDirection = field === sortField && sortDirection === 'desc' ? 'asc' : 'desc';
    navigate({ search: (prev) => ({ ...prev, sort: `${field}:${nextDirection}` }) });
  };

  const {
    isLoading,
    isRefetching,
    pagination,
    data: content,
  } = useAdminContentList({
    filterText,
    status,
    type,
    visibility,
    sortBy: sortField,
    sortDir: sortDirection,
  });

  const isSearching = useMemo(
    () => filterText.length >= MIN_CHARS_TO_DO_SEARCH && isRefetching,
    [filterText, isRefetching],
  );

  const dataWithId = useMemo(() => content ?? [], [content]);

  const tableListItems = useMemo(
    () =>
      dataWithId.map((item) => ({
        id: item.id,
        cells: [
          item.title || '/',
          item.authorSummary?.name || item.authorSummary?.email || '/',
          <ContentOriginBadge key={item.id} isPlatformContent={item.isPlatformContent} />,
          <ContentTypeBadge key={item.id} type={item.type} />,
          getContentDomainsLabel(item.domains),
          getContentVisibilityLabel(item.visibility),
          formatContentDate(item.updatedAt),
          <ContentStatusBadge key={item.id} status={item.status} />,
        ],
      })),
    [dataWithId],
  );

  return (
    <SidebarItemViewTemplate title="Content Moderation">
      <HStack width={'100%'} marginTop={'20px'} justifyContent={'space-between'}>
        <TabButtons
          options={[
            { onClick: () => setStatus('published'), labelValue: 'published', labelDisplay: 'Published' },
            { onClick: () => setStatus('removed'), labelValue: 'removed', labelDisplay: 'Removed' },
            { onClick: () => setStatus('withdrawn'), labelValue: 'withdrawn', labelDisplay: 'Withdrawn' },
            { onClick: () => setStatus('draft'), labelValue: 'draft', labelDisplay: 'Drafts' },
            { onClick: () => setStatus('all'), labelValue: 'all', labelDisplay: 'All' },
          ]}
          selectedOptionValue={status}
        />
        <ContentModerationFilters
          searchText={filterText}
          type={type}
          visibility={visibility}
          isSearching={isSearching}
          onSearchChange={setSearchText}
          onTypeChange={setType}
          onVisibilityChange={setVisibility}
        />
      </HStack>
      <CustomTable<TContentListItem[], string>
        noItemsMessage="No content found"
        columns={COLUMNS}
        isLoading={isLoading}
        isRefetching={isRefetching}
        isSearching={isSearching}
        tableListItems={tableListItems}
        pagination={pagination}
        dataWithId={dataWithId}
        customColumnGridPercentages={[22, 16, 11, 9, 11, 11, 12, 8]}
        columnsToSortBy={SORTABLE_COLUMNS}
        sortingColumn={sortingColumn}
        sortingDirection={sortDirection}
        updateSortingColumn={updateSortingColumn}
        fullWidth
        hasRowSelect
      />
    </SidebarItemViewTemplate>
  );
};

export const Route = createFileRoute('/_authenticated/_admin/content/moderation/')({
  component: ContentModeration,
  validateSearch: (search: Record<string, unknown>): ContentModerationSearch => {
    const result: ContentModerationSearch = {};

    if (CONTENT_STATUS_FILTERS.includes(search.status as TContentStatusFilter)) {
      result.status = search.status as TContentStatusFilter;
    }

    if (CONTENT_TYPES.includes(search.type as TContentType)) {
      result.type = search.type as TContentType;
    }

    if (
      CONTENT_VISIBILITY_FILTERS.includes(search.visibility as TContentVisibilityFilter) &&
      search.visibility !== 'all'
    ) {
      result.visibility = search.visibility as TContentVisibilityFilter;
    }

    const rawQ = typeof search.q === 'string' ? search.q.trim() : '';
    if (rawQ) {
      result.q = rawQ;
    }

    if (typeof search.sort === 'string' && SORT_PARAM_REGEX.test(search.sort)) {
      result.sort = search.sort;
    }

    return result;
  },
});
